import { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  styled,
  Typography,
  Alert,
} from '@mui/material';
import {
  ChevronRightOutlined as ChevronRightOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PortraitRounded as PortraitRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  PhoneIphone as PhoneIphoneIcon,
  PhoneEnabledOutlined as PhoneEnabledOutlinedIcon,
  MailOutlineRounded as MailOutlineRoundedIcon,
} from '@mui/icons-material';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { usePassengerPhone } from '@/hooks/display/usePassengerPhone';
import { useFeatureCreateReservationMemberPortal } from '@/hooks/features/useFeatureCreateReservation';
import { useAddress } from '@/hooks/display/useAddress';

import { Passenger, PassengerAddress } from '@/data/models/passenger.model';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '@/data/models/trip-leg-status.model';
import {
  TransportReservation,
  TransportTripLeg,
} from '@/data/models/transport-reservation.model';

import passengerUtils from '@/services/passengerUtils';
import reservationService from '@/services/reservationService';
import { isAbortedRequestError } from '@/services/httpService.utils';

import * as accountingSlice from '@/data/accounting.slice';
import * as passengerSlice from '@/data/passenger.slice';
import * as clientSlice from '@/data/client.slice';
import * as lookupSlice from '@/data/lookup.slice';
import * as reservationSlice from '@/data/reservation.slice';
import * as userSlice from '@/data/user.slice';
import {
  fetchFutureReservationsAsync,
  selectLoadedFutureReservations,
  selectUpcomingReservations,
} from '@/data/slices/reservation.slice';

import ReservationDialog from '@/components/home/reservations/ReservationDialog';
import PassengerBenefits from '@/components/home/passengers/PassengerBenefits';
import TripCard from '@/components/member/shared/TripCard';
import { CreateReservationDialog } from '@/components/reservations/CreateReservationDialog';
import { DateTime } from 'luxon';
import { ReservationEventType } from '@/data/events/reservation';
import * as tripLegSlice from '@/data/tripleg.slice';
import { TripLegEventType } from '@/data/events/trip-leg';

const PREFIX = 'MemberHome';

const classes = {
  headingSection: `${PREFIX}-heading-section`,
  firstExperienceContainer: `${PREFIX}-first-experience-container`,
  bookATripContainer: `${PREFIX}-book-a-trip-container`,
  bookATripButton: `${PREFIX}-book-a-trip-button`,
  recentTripContainer: `${PREFIX}-recent-trip-container`,
  recentTripItem: `${PREFIX}-recent-trip-item`,
  tripCardContent: `${PREFIX}-trip-card-content`,
  tripRow: `${PREFIX}-trip-row`,
  tripMapContainer: `${PREFIX}-trip-map-container`,
  tripMapSpacer: `${PREFIX}-trip-map-spacer`,
  tripMapDropShadow: `${PREFIX}-trip-map-drop-shadow`,
  tripMapOverlay: `${PREFIX}-trip-map-overlay`,
  tripOuterContainer: `${PREFIX}-trip-outer-container`,
  tripHeaderContainer: `${PREFIX}-trip-header-container`,
  tripContainer: `${PREFIX}-trip-container`,
  tripItem: `${PREFIX}-trip-item`,
  tripItemConnector: `${PREFIX}-trip-item-connector`,
  sectionLink: `${PREFIX}-section-link`,
  profileOuterContainer: `${PREFIX}-profile-outer-container`,
  profileIcon: `${PREFIX}-profile-icon`,
  profileLabel: `${PREFIX}-profile-label`,
  empty: `${PREFIX}-empty`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.headingSection}`]: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.625),
    marginBottom: theme.spacing(-3),
  },

  [`&.${classes.firstExperienceContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    '& .disc-list': {
      paddingLeft: theme.spacing(4),
      listStyleType: 'disc',
      '& .MuiListItem-root': {
        padding: 0,
      },
      '& .MuiListItemText-root': {
        display: 'list-item',
      },
    },
  },

  [`&.${classes.bookATripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },

  [`& .${classes.bookATripButton}`]: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'auto',
    },
  },

  [`&.${classes.recentTripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`&.${classes.recentTripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(5),
    paddingTop: theme.spacing(1),
    [`& p`]: {
      flex: 1,
    },
    [`& button`]: {
      flex: 'none',
    },
  },

  [`&.${classes.tripCardContent}`]: {
    [`& .section-link`]: {
      textDecoration: 'none',
      color: 'unset',
      pointerEvents: 'cursor',
    },
  },

  [`&.${classes.tripRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`&.${classes.tripMapContainer}`]: {
    flex: 1,
    display: 'flex',
  },

  [`&.${classes.tripMapDropShadow}`]: {
    filter: `drop-shadow(0px 2px 7px ${theme.palette.lightActionDisabled15p.main})`,
    width: '100%',
    minWidth: '170px',
    maxWidth: '483px',
  },

  [`&.${classes.tripMapSpacer}`]: {
    flex: 1,
    minWidth: 20,
    [theme.breakpoints.up('md')]: {
      minWidth: 50,
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: 75,
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 100,
    },
  },

  [`&.${classes.tripMapOverlay}`]: {
    marginTop: '-233px',
    marginBottom: '233px',
    height: '100%',
    position: 'relative',
    background: 'transparent',
    zIndex: 1000,
  },

  [`&.${classes.tripOuterContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },

  [`&.${classes.tripContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    [`& .${classes.tripItem}:first-of-type`]: {
      marginTop: theme.spacing(1.5),
    },
  },

  [`&.${classes.tripHeaderContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },

  [`&.${classes.tripItem}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1.5),
    [`& svg`]: {
      backgroundColor: 'white',
    },
  },

  [`&.${classes.tripItemConnector}`]: {
    width: '3px',
    height: '100%',
    margin: '0 auto',
    marginTop: theme.spacing(-1.6),
    backgroundImage: "url('/dot-small.svg')",
  },

  [`&.${classes.profileOuterContainer}`]: {
    ['& .section-link']: {
      textDecoration: 'none',
      color: 'unset',
    },
    ['& .icon-secondary-color']: {
      fill: theme.palette.iconSecondaryColor.main,
    },
  },

  [`& .${classes.profileIcon}`]: {
    flex: 1,
    minWidth: 88,
    color: theme.palette.lightActionDisabled90p.main,
  },

  [`& .${classes.profileLabel}`]: {
    textAlign: 'end',
    minWidth: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },

  [`&.${classes.empty}`]: {
    height: theme.spacing(1.25),
  },
}));

const HomePage = () => {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const activeStatus = [
    TripLegStatus.Confirmed.toString(),
    TripLegStatus.InTransit.toString(),
    TripLegStatus.Onsite.toString(),
    TripLegStatus.Onboard.toString(),
  ];
  const [activeReservations, setActiveReservations] = useState<
    TransportReservation[]
  >([]);
  const [passenger, setPassenger] = useState<Passenger | null>(null);
  const passengerFetched = useAppSelector(
    passengerSlice.selectCurrentPassenger,
  );
  const futureReservations = useAppSelector(selectUpcomingReservations);
  const futureReservationsLoaded = useAppSelector(
    selectLoadedFutureReservations,
  );
  const passengerClientData = useAppSelector(
    clientSlice.selectPassengerClientData,
  );
  const passengerClientDataLoaded = useAppSelector(
    clientSlice.selectLoadedPassengerClientData,
  );
  const reservationEventEmitted = useAppSelector(
    reservationSlice.selectReservationEventEmitted,
  );
  const tripLegEventEmitted = useAppSelector(
    tripLegSlice.selectTripLegEventEmitted,
  );
  const passengerEventEmitted = useAppSelector(
    passengerSlice.selectPassengerEventEmitted,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const userInfo = useAppSelector(userSlice.selectLoggedInUserInfo);

  // TODO: Remove old reservation dialog state when Feature Flag is phased out
  const [openReservationDialog, setOpenReservationDialog] = useState(false);
  const [openCreateReservationDialog, setOpenCreateReservationDialog] =
    useState(false);
  const [showFirstExperienceCard, setShowFirstExperienceCard] = useState(false);

  const featureCreateReservation = useFeatureCreateReservationMemberPortal();

  const landlinePhone = usePassengerPhone(
    passenger?.passengerPhones,
    'LANDLINE',
  );
  const naPhone = usePassengerPhone(passenger?.passengerPhones, 'NA');

  const upcomingReservations = useMemo(() => {
    if (!futureReservations.items || futureReservations.items.length === 0)
      return [];

    const legDate = (leg: TransportTripLeg) =>
      DateTime.fromISO(
        leg?.pickUpDueTimeUTC ??
          leg?.appointmentTimeUTC ??
          leg?.appointmentDate ??
          DateTime.utc().toISO(),
      ).toSeconds();

    const results = futureReservations.items
      .filter((res) => !activeReservations.includes(res))
      .flatMap((reservation) =>
        reservation.tripLegs.map((leg, legIndex) => ({
          reservation,
          legIndex,
          sortKey: legDate(leg),
        })),
      )
      // sort by oldest first
      .sort((a, b) => a.sortKey - b.sortKey)
      // only display today and future trips
      .filter((a) => a.sortKey >= DateTime.local().startOf('day').toSeconds());
    return results;
  }, [futureReservations, activeReservations]);

  const handleOpenCreateReservationDialog = () => {
    // TODO: Remove feature flag
    if (featureCreateReservation) {
      setOpenCreateReservationDialog(true);
    } else {
      setOpenReservationDialog(true);
    }
  };

  const handleCloseCreateReservationDialog = () => {
    // TODO: Remove feature flag
    if (featureCreateReservation) {
      setOpenCreateReservationDialog(false);
    } else {
      setOpenReservationDialog(false);
    }
    if (passenger) {
      dispatch(
        clientSlice.fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
    }
  };

  const retrievePassengerAddress = (addresses: PassengerAddress[]) => {
    return addresses.find((addr) => addr.passengerAddressUseBC === 'MANUAL')
      ? useAddress(
          addresses.find((addr) => addr.passengerAddressUseBC === 'MANUAL'),
        )
      : useAddress(addresses[0]);
  };

  const reservationRequestRef = useRef<AbortController | null>(null);
  const passengerRequestRef = useRef<AbortController | null>(null);
  const fetchFutureReservations = () => {
    if (!passenger) return;
    reservationRequestRef.current?.abort();
    const abortController = new AbortController();
    reservationRequestRef.current = abortController;
    dispatch(
      fetchFutureReservationsAsync(
        {
          passengerUUIDs: [passenger.passengerUUID!],
          page: 1,
          pageSize: 20,
          includePassenger: true,
          includeTripLegs: true,
          sort: 'reservationServiceDate',
          sortOrder: 'asc',
          reservationState: 'active',
          tripLegsState: ['active'],
          pickupDateFrom: DateTime.local().startOf('day').toISODate(),
        },
        reservationRequestRef.current.signal,
      ),
    );
  };

  const fetchPassengerBenefits = () => {
    passengerRequestRef.current?.abort();
    const abortController = new AbortController();
    passengerRequestRef.current = abortController;
    dispatch(
      passengerSlice.fetchPassengerAsync(
        passenger?.passengerUUID!,
        passengerRequestRef.current.signal,
      ),
    );
  };

  // prevent passenger getting cleared on every refetch
  useEffect(() => {
    if (!!passengerFetched) setPassenger(passengerFetched);
  }, [passengerFetched]);

  useEffect(() => {
    if (passenger && passenger.passengerUUID) {
      dispatch(
        clientSlice.fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
          passenger.passengerUUID,
        ),
      );
      dispatch(
        passengerSlice.fetchPassengerPreferencesAsync(
          passenger.passengerUUID!,
          passenger.clientBC,
        ),
      );
      dispatch(accountingSlice.fetchTransportationAttributesAsync());
      dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
      fetchFutureReservations();
    }
  }, [passenger?.passengerUUID]);

  useEffect(() => {
    if (futureReservations.items && futureReservations.items.length > 0) {
      setActiveReservations([]);
      futureReservations.items.forEach((reservation) => {
        const legStatuses = reservation.tripLegs.flatMap(
          (x) => x.tripLegStatusBC?.toLocaleLowerCase() as TripLegStatus,
        );
        if (legStatuses.some((status) => activeStatus.includes(status!))) {
          setActiveReservations((x) => [...x, reservation]);
        }
      });
    }
  }, [futureReservations]);

  // F1V2-6329: The card shows from the member's first session, regardless of
  // trips booked before it (Client Portal / F1), until a new trip is booked in any portal.
  // Keyed by passenger UUID: after a client-side logout/login the previous
  // member's passenger and trips can linger in the store for a render.
  const bookedSinceFirstLoginForRef = useRef<string | null>(null);
  useEffect(() => {
    const passengerUUID = passenger?.passengerUUID;
    if (
      !passengerUUID ||
      bookedSinceFirstLoginForRef.current === passengerUUID ||
      !userInfo?.createdOnUTC ||
      !futureReservationsLoaded
    ) {
      return;
    }

    // The users/me createdOn is from the userAccount table.
    // A member's userAccount is created when they first log in.
    const firstLogin = DateTime.fromISO(userInfo.createdOnUTC, { zone: 'utc' });
    const isCreatedSinceFirstLogin = (createdOnUTC?: string) => {
      return (
        !!createdOnUTC &&
        DateTime.fromISO(createdOnUTC, { zone: 'utc' }) >= firstLogin
      );
    };

    // Scoped to this passenger, since the loaded list can still be the
    // previous member's on the render where the passenger changes
    const bookedSinceFirstLoginOnFutureTrips = futureReservations.items.some(
      (reservation) =>
        reservation.passenger?.passengerUUID === passengerUUID &&
        isCreatedSinceFirstLogin(reservation.createdOnUTC),
    );

    // Already loaded, so check future trips first and skip the query on a hit
    if (bookedSinceFirstLoginOnFutureTrips) {
      bookedSinceFirstLoginForRef.current = passengerUUID;
      setShowFirstExperienceCard(false);
      return;
    }

    const abortController = new AbortController();
    // get the latest created reservation. Future trips may not contain this value,
    // since after being completed they are moved to past trips
    const subscription = reservationService
      .getPassengerTransportReservations(
        {
          passengerUUIDs: [passengerUUID],
          page: 1,
          pageSize: 1,
          reservationState: 'active,inactive',
          sort: 'createdOnUTC',
          sortOrder: 'desc',
        },
        abortController.signal,
      )
      .subscribe({
        next: (response) => {
          const bookedSinceFirstLogin = isCreatedSinceFirstLogin(
            response.items?.[0]?.createdOnUTC,
          );
          if (bookedSinceFirstLogin) {
            bookedSinceFirstLoginForRef.current = passengerUUID;
          }
          setShowFirstExperienceCard(!bookedSinceFirstLogin);
        },
        error: (err) => {
          if (isAbortedRequestError(err)) return;
          setShowFirstExperienceCard(false);
        },
      });

    return () => {
      abortController.abort();
      subscription.unsubscribe();
    };
  }, [
    passenger?.passengerUUID,
    userInfo?.createdOnUTC,
    futureReservationsLoaded,
    futureReservations.items,
  ]);

  /* Begin Realtime Event Handling */
  useEffect(() => {
    if (tripLegEventEmitted) {
      // If any tripleg event is emitted, we need to refetch reservations
      const eventTypesToTrack = [
        TripLegEventType.TripLegOpened,
        TripLegEventType.TripLegCanceled,
        TripLegEventType.TripLegCompleted,
        TripLegEventType.TripLegNotLoaded,
        TripLegEventType.TripLegCanceledLate,
      ];
      if (eventTypesToTrack.includes(tripLegEventEmitted.detailType)) {
        fetchFutureReservations();
        fetchPassengerBenefits();
        dispatch(tripLegSlice.setTripLegEventEmitted(null));
      }
    }
  }, [tripLegEventEmitted]);

  useEffect(() => {
    if (reservationEventEmitted) {
      // If any reservation event is emitted, we need to refetch the benefits summary,
      // but not refetch reservations since we handle that on the trip leg event handling above
      const eventTypesToIgnore = [
        ReservationEventType.ReservationDrafted,
        ReservationEventType.ReservationActivatedSMS,
      ];
      if (!eventTypesToIgnore.includes(reservationEventEmitted.detailType)) {
        fetchFutureReservations();
        fetchPassengerBenefits();
        dispatch(reservationSlice.setReservationEventEmitted(null));
      }
    }
  }, [reservationEventEmitted]);

  useEffect(() => {
    if (passengerEventEmitted) {
      // All events coming in are already scoped to the current passenger, we should
      // just update the current passenger
      dispatch(passengerSlice.fetchPassengerAsync(passenger?.passengerUUID!));
      dispatch(passengerSlice.setPassengerEventEmitted(null));
    }
  }, [passengerEventEmitted]);
  /* End Realtime Event Handling */

  const showNoBenefitBanner = useMemo(() => {
    if (
      passengerClientDataLoaded &&
      passengerClientData &&
      passengerClientData.servicePlans
    ) {
      return passengerUtils.showNoBenefitBanner(
        passengerClientData.servicePlans,
      );
    }
    return false;
  }, [passenger, passengerClientData.servicePlans, passengerClientDataLoaded]);

  return (
    <>
      <Grid container spacing={3}>
        {showNoBenefitBanner && (
          <Grid item xs={12}>
            <Alert variant="outlined" severity="error" className="site-banner">
              {t('member.noBenefitBanner.title')}
              <Typography variant="body2">
                {t('member.noBenefitBanner.message')}
              </Typography>
              <Typography variant="body2">
                {t('member.noBenefitBanner.disclaimer')}{' '}
                <strong>{t('footer.phoneNumber')}</strong>
              </Typography>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} md={7} lg={8} xl={9}>
          <Stack direction="column" spacing={3}>
            {showFirstExperienceCard && (
              <Card>
                <CardContent>
                  <Stack direction="column" spacing={{ xs: 2, md: 4 }}>
                    <Typography gutterBottom variant="h1">
                      {passenger?.firstName}, {t('member.portalHeading')}
                    </Typography>
                    <Stack
                      direction={{
                        xs: 'column-reverse',
                        sm: 'column-reverse',
                        md: 'column-reverse',
                        lg: 'row',
                      }}
                      spacing={{ xs: 2, sm: 3, md: 5, lg: 6 }}>
                      <StyledBox className={classes.firstExperienceContainer}>
                        <Typography variant="h5" component="p">
                          {t('member.portalSubHeading')}
                        </Typography>
                        <List className="disc-list" dense>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  {t('member.bookTrips')}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  {t('member.monitorTripStatus')}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  {t('member.checkEligibility')}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Typography variant="body1">
                                  {t('member.andMore')}
                                </Typography>
                              }
                            />
                          </ListItem>
                        </List>
                      </StyledBox>
                      {/* TODO https://americanlogistics.atlassian.net/browse/F1V2-880 commenting out the video player for upcoming release when video content is finalized */}
                      {/*<Stack flex={1}></Stack>*/}
                      {/*<StyledBox>*/}
                      {/*  <img*/}
                      {/*    src={videoPlayerImage}*/}
                      {/*    className="home-video-player"*/}
                      {/*    alt={t('member.videoPlayerPlaceholder')}*/}
                      {/*  />*/}
                      {/*</StyledBox>*/}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {activeReservations.length > 0 && (
              <StyledBox className={classes.headingSection}>
                <Typography variant="h3">{t('member.live')}</Typography>
              </StyledBox>
            )}

            {activeReservations.map(
              (reservation) =>
                reservation.tripLegs &&
                reservation.tripLegs.map((_leg, legIndex) => {
                  if (
                    _leg.tripLegStatusBC &&
                    activeStatus.includes(
                      _leg.tripLegStatusBC.toLocaleLowerCase(),
                    )
                  ) {
                    return (
                      <TripCard
                        key={reservation.tripLegs[legIndex].tripLegReferenceID}
                        legIndex={legIndex}
                        tripLegReferenceID={
                          reservation.tripLegs[legIndex].tripLegReferenceID
                        }
                        enableTracking={true}
                      />
                    );
                  }
                }),
            )}

            {!!passengerClientData && !showNoBenefitBanner && (
              <Card>
                <CardContent>
                  <StyledBox className={classes.bookATripContainer}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Stack direction="column" flex={1} spacing={1}>
                        <Typography variant="h2">
                          {t('member.bookANewTrip')}
                        </Typography>
                      </Stack>
                      <StyledBox className={classes.bookATripButton}>
                        <Button
                          variant="contained"
                          size="large"
                          endIcon={<ChevronRightOutlinedIcon />}
                          onClick={handleOpenCreateReservationDialog}
                          disabled={!passengerClientData || !passenger}
                          data-fs-element="BookaTripStartButton"
                          sx={{
                            width: { xs: '100%', md: 'auto' },
                            mt: { xs: 3, md: 0 },
                          }}>
                          {t('member.bookATrip')}
                        </Button>
                      </StyledBox>
                    </Stack>
                  </StyledBox>
                </CardContent>
              </Card>
            )}

            {upcomingReservations.length > 0 && (
              <StyledBox className={classes.headingSection}>
                <Typography variant="h3">{t('member.upcoming')}</Typography>
              </StyledBox>
            )}

            {/* Showing only first 4 future reservations based on business requirement; Not modified the API call's size
            because, the live trips are not always coming at the top of the api response. */}
            {upcomingReservations
              .filter(
                ({ reservation, legIndex }) =>
                  reservation.tripLegs[legIndex].tripLegStatusBC !==
                  TripLegStatusBC.Completed,
              )
              .slice(0, 4)
              .map(({ reservation, legIndex }) => (
                <TripCard
                  key={reservation.tripLegs[legIndex].tripLegReferenceID}
                  legIndex={legIndex}
                  tripLegReferenceID={
                    reservation.tripLegs[legIndex].tripLegReferenceID
                  }
                  enableTracking={false}
                />
              ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={5} lg={4} xl={3}>
          <Stack direction="column" spacing={3}>
            {passenger && (
              <PassengerBenefits
                passenger={passenger}
                servicePlans={passengerClientData?.servicePlans || []}
                isMemberPortal={true}
              />
            )}

            <StyledBox className={classes.profileOuterContainer}>
              <NavLink to="/settings" className="section-link">
                <Card>
                  <CardContent>
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={0}>
                        <Typography variant="h3">
                          {t('member.profile')}
                        </Typography>
                        <ChevronRightOutlinedIcon className="icon-secondary-color" />
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PortraitRoundedIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.memberId')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          className={classes.profileLabel}
                          component="p">
                          {passenger?.clientPassengerID}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonOutlineRoundedIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.name')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          className={classes.profileLabel}
                          component="p">
                          {passenger?.firstName} {passenger?.lastName}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PlaceOutlinedIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.address')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          className={classes.profileLabel}
                          component="p">
                          {passenger?.passengerAddresses &&
                            retrievePassengerAddress(
                              passenger.passengerAddresses,
                            )}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PhoneEnabledOutlinedIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.phone')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          className={classes.profileLabel}
                          component="p">
                          {landlinePhone || naPhone}
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PhoneIphoneIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.mobile')}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          className={classes.profileLabel}
                          component="p">
                          {usePassengerPhone(
                            passenger?.passengerPhones,
                            'MOBILE',
                          )}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MailOutlineRoundedIcon className="icon-secondary-color" />
                        <Typography
                          variant="body2"
                          className={classes.profileIcon}>
                          {t('member.email')}
                        </Typography>
                        {passenger?.passengerEmailAddress && (
                          <Typography
                            variant="subtitle2"
                            className={classes.profileLabel}
                            component="p">
                            {passenger.passengerEmailAddress}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </NavLink>
            </StyledBox>
          </Stack>
        </Grid>
      </Grid>
      {passenger && openReservationDialog && (
        <ReservationDialog
          open={openReservationDialog}
          close={handleCloseCreateReservationDialog}
          passenger={passenger!}
          title={t('member.bookATrip')}
          servicePlans={passengerClientData?.servicePlans || []}
          isPortal={true}
        />
      )}
      {passenger && openCreateReservationDialog && (
        <CreateReservationDialog
          open={openCreateReservationDialog}
          onClose={handleCloseCreateReservationDialog}
          passenger={passenger!}
          servicePlans={passengerClientData?.servicePlans || []}
          transportationAttributes={transportationAttributes}
        />
      )}
    </>
  );
};

export default HomePage;
